import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import OnboardingOverlayCard from './OnboardingOverlayCard';
import OnboardingPagerFooter from './OnboardingPagerFooter';

const SLIDE_MS = 300;

export interface OnboardingSlideStepContext {
  active: boolean;
}

export interface OnboardingSlidePagerStep {
  key: string;
  content: React.ReactNode | ((ctx: OnboardingSlideStepContext) => React.ReactNode);
}

interface OnboardingSlidePagerProps {
  title: string;
  steps: OnboardingSlidePagerStep[];
  nextLabel?: string;
  doneLabel?: string;
  onComplete: () => void;
}

/**
 * Stable overlay card with a sliding body. Title + footer stay mounted;
 * only the step content translates between pages — no exit/unmount flash.
 */
const OnboardingSlidePager: React.FC<OnboardingSlidePagerProps> = ({
  title,
  steps,
  nextLabel = 'Next',
  doneLabel = 'Done',
  onComplete,
}) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [viewportHeight, setViewportHeight] = useState<number | undefined>();
  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);

  const stepCount = steps.length;
  const safeIndex =
    stepCount === 0 ? 0 : Math.min(Math.max(stepIndex, 0), stepCount - 1);
  const isLast = stepCount === 0 || safeIndex >= stepCount - 1;

  useLayoutEffect(() => {
    if (stepIndex !== safeIndex) {
      setStepIndex(safeIndex);
    }
  }, [safeIndex, stepIndex]);

  useLayoutEffect(() => {
    const activeSlide = slideRefs.current[safeIndex];
    if (!activeSlide) return;

    const updateHeight = () => setViewportHeight(activeSlide.offsetHeight);
    updateHeight();

    // Videos / async content can change slide height after first paint.
    const observer = new ResizeObserver(updateHeight);
    observer.observe(activeSlide);
    return () => observer.disconnect();
  }, [safeIndex, steps]);

  const goToStep = useCallback(
    (index: number) => {
      if (index < 0 || index >= stepCount || index === safeIndex) return;
      setStepIndex(index);
    },
    [safeIndex, stepCount],
  );

  const handleNext = () => {
    if (isLast) {
      onComplete();
      return;
    }
    setStepIndex((current) => Math.min(current + 1, stepCount - 1));
  };

  return (
    <OnboardingOverlayCard
      title={title}
      footer={
        <OnboardingPagerFooter
          stepCount={stepCount}
          activeIndex={safeIndex}
          onNext={handleNext}
          onDotClick={goToStep}
          nextLabel={nextLabel}
          doneLabel={doneLabel}
          isLast={isLast}
        />
      }
    >
      <Box
        sx={{
          overflow: 'hidden',
          width: '100%',
          height: viewportHeight,
          transition: viewportHeight == null ? undefined : `height ${SLIDE_MS}ms ease`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            transition: `transform ${SLIDE_MS}ms ease`,
            transform: `translateX(-${safeIndex * 100}%)`,
          }}
        >
          {steps.map((step, index) => {
            const active = index === safeIndex;
            const content =
              typeof step.content === 'function'
                ? step.content({ active })
                : step.content;

            return (
              <Box
                key={step.key}
                ref={(node: HTMLDivElement | null) => {
                  slideRefs.current[index] = node;
                }}
                sx={{
                  minWidth: '100%',
                  width: '100%',
                  boxSizing: 'border-box',
                  pointerEvents: active ? 'auto' : 'none',
                }}
                aria-hidden={!active}
              >
                {content}
              </Box>
            );
          })}
        </Box>
      </Box>
    </OnboardingOverlayCard>
  );
};

export default OnboardingSlidePager;
